import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Col, Form, Input, Select } from 'antd';

import FloatingFormItem from '@/components/_common/FloatingFormItem';
import CnpjFormField from '@/components/_common/CnpjFormField';
import {
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
} from '@/hooks';
import { InputDate } from '@/components/_common';
import { TypeOfTaxpayers } from '@/core/domain/invoice/invoice/Invoice.types';
import { isAppPortocar } from '@/index';
import { useCustomerFormContext } from '../CustomerFormProvider';
import { validateCnpj } from '@/utils/functions/validateCnpj';

interface Props {
  isEdit?: boolean;
}

function LegalPersonFields(props: Props) {
  const form = Form.useFormInstance();
  const ruleRequired = useDefaultEmptyFieldError();
  const intl = useIntl();

  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();

  const { customer } = useCustomerFormContext();

  const typeOfTaxpayersOptions = [
    {
      label: 'Contribuinte ICMS',
      value: TypeOfTaxpayers.icmsTaxPayer,
    },
    {
      label: 'Contribuinte Isento',
      value: TypeOfTaxpayers.exemptTaxPayer,
    },
    {
      label: 'Não contribuinte',
      value: TypeOfTaxpayers.nonContributor,
    },
  ];

  const cpfCnpj = Form.useWatch('cpfCnpj', form);
  const foreignDocument = Form.useWatch('foreignDocument', form);

  const isValidCnpj = validateCnpj(customer?.cpfCnpj || '');

  return (
    <>
      {!foreignDocument && (
        <Col xs={24} sm={12} md={6}>
          <CnpjFormField
            label="CNPJ"
            fieldName="cpfCnpj"
            required={isAppPortocar}
            disabled={!!props.isEdit && isAppPortocar && !!isValidCnpj}
          />
        </Col>
      )}

      {!cpfCnpj && (
        <Col xs={24} sm={12} md={6}>
          <FloatingFormItem
            name="foreignDocument"
            label="Doc. identificação estrangeiro"
            rules={isAppPortocar && !cpfCnpj ? [ruleRequired] : []}
          >
            <Input
              disabled={
                !!props.isEdit && isAppPortocar && !!customer?.foreignDocument
              }
            />
          </FloatingFormItem>
        </Col>
      )}

      <Col xs={24} sm={24} md={6}>
        <FloatingFormItem
          name="name"
          label={intl.formatMessage({ id: 'customers.domain.name' })}
          rules={[ruleRequired, defaultMaxLenghtFieldError(100)]}
        >
          <Input />
        </FloatingFormItem>
      </Col>

      <Col xs={24} sm={24} md={6}>
        <FloatingFormItem label="Tipo de contribuinte" name="taxpayerType">
          <Select options={typeOfTaxpayersOptions} />
        </FloatingFormItem>
      </Col>

      <Col xs={24} sm={12} md={6}>
        <FloatingFormItem
          name="fantasyName"
          label={intl.formatMessage({ id: 'customers.domain.fantasyName' })}
          rules={[defaultMaxLenghtFieldError(100)]}
        >
          <Input />
        </FloatingFormItem>
      </Col>

      <Col xs={24} sm={12} md={8}>
        <FloatingFormItem
          name="municipalRegistration"
          label={intl.formatMessage({
            id: 'customers.domain.municipalRegistration',
          })}
          rules={[defaultMaxLenghtFieldError(15)]}
        >
          <Input />
        </FloatingFormItem>
      </Col>

      <Col xs={24} sm={24} md={8}>
        <FloatingFormItem
          name="stateRegistration"
          label={intl.formatMessage({
            id: 'customers.domain.stateRegistration',
          })}
          rules={[defaultMaxLenghtFieldError(25)]}
        >
          <Input />
        </FloatingFormItem>
      </Col>

      <Col xs={24} sm={8}>
        <FloatingFormItem name="creation" label="Data de Criação">
          <InputDate disabled />
        </FloatingFormItem>
      </Col>
    </>
  );
}

LegalPersonFields.propTypes = {
  form: PropTypes.object.isRequired,
};

export default LegalPersonFields;
